import { pinFoot } from './pin-foot'
import { extrasOf } from './extras'
import { currencySymbol, currencyWord } from './round-config'
import { api, dismiss, getQueue, mutate, readCache, type Ack } from './api'
import { offerUndo, undoPatch } from './undo'
import { dayLabel, el, money, parseMoney, toast } from './ui'
import { hasInsides, outsidePence, priceWords } from './inside-price'
import { sheet } from './customer-sheets'
import { cursorOnOpen } from './sheet-keyboard'
import { loadRoundJobs, roundJobs, severalJobsOn, visitJobLabel } from './job-names'
import { isWorker } from './me'
import type { CustomerJob } from './jobs'
import type { Clean, Customer } from './types'
import './price-sheet.css'

// Tap a visit's price to change it (Liam 2026-09-28: a customer paid a
// different amount because the price was changed with her on WhatsApp, and the
// diary's "Change price, worker or note…" in the ⋯ was too hidden).
//
// The Diary, phone and computer (tidy-up 2, item 2; Liam, 20 Oct 2026: "Price isnt tappable if we change there we need
// to decide one off or change regualr cycle"; drawn and approved, docs/plans/tidy-up-2/AGREED.md 2): the price opens a
// sheet, "{Name}'s price": the price box, then Just this visit (only this visit; their usual price stays) or This visit
// and every one after (this visit, the job's usual price, and every later booked visit of that job still at the old
// price). Undo after either. The sheet opens in a dialog of its own, so it shows wherever the row's button sits (the
// phone's row lends its button out of the hidden details, and the box used to open in there, unseen).
//
// Owed keeps the box in place (opts.inline): "£ [25.00] Save Cancel", that one visit only. Enter saves, Escape cancels.

export interface PricedVisit {
  id: number
  version: number
  amount_pence: number | null | undefined
  // The insides part of it (0070): the button reads "Outside £45 · Inside £40" and changes the outside price only.
  inside_pence?: number | null
  // Extras for this visit (0087): changed from the visit itself for now (Edit visit), not here (Astra's extras check 7).
  extras_json?: string | null
  date: string
  customer_name?: string | null
  // What decides whether the price can be carried on (the same rule as the server's, functions/lib/price-onward.ts).
  customer_id?: number
  state?: string | null
  kind?: string | null
  job_id?: number | null
  job_name?: string | null
  part?: string | null
  rest_of?: number | null
  carryover?: number | null
}

// A price change for this visit still waiting in the phone's queue (no signal): the whole price it will have. The row
// shows it, after a redraw and a restart too (Sol R09, Sonnet F5); a refused one is not waiting for anything.
function waitingWhole(id: number): number | null | undefined {
  const path = `/api/cleans/${id}`
  const body = getQueue()
    .filter((m) => m.method === 'PATCH' && m.path === path && (m.status === 'queued' || m.status === 'parked' || m.status === 'signed-out' || (m.status === 'sending' && !!m.error)))
    .map((m) => m.body)
    .filter((b): b is Record<string, unknown> => typeof b === 'object' && b !== null && 'amount_pence' in b)
    .at(-1)
  return body ? ((body.amount_pence as number | null) ?? null) : undefined
}

const waitingWords = (whole: number | null, inside: number | null | undefined): string =>
  `${priceWords({ amount_pence: whole, inside_pence: inside ?? null }) ?? 'No price'} · waiting for signal`

export function priceButton(cl: PricedVisit, opts: { className?: string; after: () => void; inline?: boolean }): HTMLElement {
  const holder = el('span', { class: 'price-edit' })
  const who = cl.customer_name ?? 'this visit'
  const waiting = opts.inline ? undefined : waitingWhole(cl.id)
  const label = waiting !== undefined ? waitingWords(waiting, cl.inside_pence) : (priceWords(cl) ?? 'Add price')
  // A visit with the insides done as well: the box changes the outside price, the insides stay as they are (0070).
  const insides = hasInsides(cl) ? (cl.inside_pence as number) : 0
  const shown = insides ? outsidePence(cl) : (cl.amount_pence ?? null)
  const show = el('button', { type: 'button', class: `price-edit-btn ${opts.className ?? ''}`.trim(), 'aria-label': `${label}: change the price for ${who}`, title: 'Change the price for this visit' })
  const words = el('span', {}, label)
  show.append(words, el('span', { class: 'price-edit-pencil', 'aria-hidden': 'true' }, '✎'))
  holder.append(show)
  // Never the start of a drag or a row tap on the diary.
  for (const ev of ['pointerdown', 'mousedown', 'touchstart'] as const) show.addEventListener(ev, (e) => e.stopPropagation())
  show.addEventListener('click', (e) => {
    e.stopPropagation()
    e.preventDefault()
    // A visit with extras: its price and extras change together from the visit (Edit visit on the Diary), so this box
    // never folds the extras into the job's own price.
    if (extrasOf(cl.extras_json).length) { toast(`${who} has extras on this visit. Change its price from the visit: Diary, ${cl.customer_name ?? 'the visit'}, Edit visit.`); return }
    if (opts.inline) openEditor()
    else openPriceSheet(cl, opts.after, (whole) => { words.textContent = waitingWords(whole, cl.inside_pence) })
  })

  function openEditor(): void {
    const form = el('form', { class: 'price-edit-form' })
    const input = el('input', { type: 'text', inputmode: 'decimal', class: 'price-edit-input', 'aria-label': `New ${insides ? 'outside ' : ''}price for ${who}, in ${currencyWord()}`, autocomplete: 'off' }) as HTMLInputElement
    input.value = shown === null || shown === undefined ? '' : (shown / 100).toFixed(2)
    const save = el('button', { type: 'submit', class: 'btn-primary price-edit-save' }, 'Save')
    const cancel = el('button', { type: 'button', class: 'price-edit-cancel' }, 'Cancel')
    const err = el('span', { class: 'price-edit-error', role: 'alert' })
    form.append(el('span', { class: 'price-edit-pound', 'aria-hidden': 'true' }, insides ? `Outside ${currencySymbol()}` : currencySymbol()), input, save, cancel, err)
    for (const ev of ['click', 'pointerdown', 'mousedown', 'touchstart'] as const) form.addEventListener(ev, (e) => e.stopPropagation())
    // Back to the price, with the keyboard where it was (Codex review 2026-09-28).
    const close = (): void => { form.replaceWith(holder); show.focus() }
    cancel.addEventListener('click', close)
    form.addEventListener('keydown', (e) => { if (e.key === 'Escape') { e.preventDefault(); close() } })
    form.addEventListener('submit', (e) => {
      e.preventDefault()
      const pence = typedPrice(input.value, (m) => { err.textContent = m; input.focus() })
      if (pence === undefined) return
      if (pence === (shown ?? null)) { close(); return }
      const whole = pence + insides
      save.setAttribute('disabled', 'disabled')
      input.setAttribute('disabled', 'disabled')
      const before = cl.amount_pence ?? null
      void mutate({
        label: `Price: ${cl.customer_name ?? ''} ${cl.date}`,
        method: 'PATCH',
        path: `/api/cleans/${cl.id}`,
        body: { version: cl.version, amount_pence: whole },
        onAck: () => opts.after(),
        onConflict: () => opts.after(),
      }).then((ack) => {
        // No signal: the change waits in the phone's queue and saves itself later (the screen
        // redraws then). Say so rather than leave a greyed-out box.
        if (!ack.ok && !(ack.status >= 400 && ack.status < 500)) {
          form.replaceWith(el('span', { class: 'price-edit-pending' }, waitingWords(whole, insides || null)))
          return
        }
        if (!ack.ok && ack.status >= 400 && ack.status < 500) {
          save.removeAttribute('disabled')
          input.removeAttribute('disabled')
          err.textContent = ack.status === 409 ? 'This visit changed somewhere else. Reload and try again.' : "Couldn't save the price. Try again."
          return
        }
        undoPatch({ text: `Changed ${who}'s ${insides ? 'outside ' : ''}price to ${money(pence)}`, label: `Undo price: ${cl.customer_name ?? ''} ${cl.date}`, path: `/api/cleans/${cl.id}`, ack, version: cl.version,
          body: { amount_pence: before }, after: () => opts.after() })
      })
    })
    holder.replaceWith(form)
    input.focus()
    input.select()
  }
  return holder
}

// The same sheet from a menu (Today's job under way, its More; Liam, 22 Oct 2026: "As the owner and worker i cant change
// price for this on the go?"). The screen redraws after, so a change waiting for signal shows there.
export function openVisitPrice(cl: PricedVisit, after: () => void): void {
  openPriceSheet(cl, after, () => after())
}

// The typed price in pence, or undefined with the words for what is wrong.
function typedPrice(raw: string, wrong: (words: string) => void): number | undefined {
  let pence: number | null
  try {
    pence = parseMoney(raw)
  } catch (ex) {
    wrong((ex as Error).message)
    return undefined
  }
  if (pence === null) { wrong('Put in a price, e.g. 25 or 25.50'); return undefined }
  if (pence > 10_000_00) { wrong(`That is over ${currencySymbol()}10,000. Check the number.`); return undefined }
  return pence
}

// ---- "This visit and every one after" -------------------------------------------------------------------------------

// Whose usual price this visit's job has: the customer's (job 1) or an extra job's (several jobs, 0042).
interface Usual { owner: 'customer' | 'job'; id: number; pence: number | null; version: number; firstClean: number | null }
// Either the price can be carried on, or why this visit changes on its own.
type Offer = { usual: Usual; only: null } | { usual: Usual | null; only: string }

// The same rule as the server's (functions/lib/price-onward.ts onwardRefusal and planOnward; Astra F10, Sol R16): a
// booked visit, not done in parts, of a running job with a repeat; job 1 is job_id null, and with several jobs on only
// its window cleaning (gutters booked on job 1 are a one-off, Sonnet F9). A worker never carries a price on.
function offerFor(cl: PricedVisit, c: Customer | null | undefined, jobs: CustomerJob[]): Offer {
  const job = typeof cl.job_id === 'number' ? jobs.find((j) => j.id === cl.job_id && !j.deleted_at) ?? null : null
  const usual: Usual | null = typeof cl.job_id === 'number'
    ? (job ? { owner: 'job', id: job.id, pence: job.price_pence ?? null, version: job.version, firstClean: null } : null)
    : (c ? { owner: 'customer', id: c.id, pence: c.reg_price_pence ?? null, version: c.version, firstClean: c.first_clean_price_pence ?? null } : null)
  const stays = usual?.pence != null ? `, so their usual price stays ${money(usual.pence)}` : ''
  const only = (why: string): Offer => ({ usual, only: `This visit only: ${why}.` })
  if (isWorker()) return only('the owner changes usual prices')
  if (cl.state !== 'booked') return only(`it is ${cl.state === 'done' ? 'done' : 'not booked'}${stays}`)
  if (cl.part || (cl.rest_of !== null && cl.rest_of !== undefined) || cl.carryover === 1) return only(`it is done in parts${stays}`)
  if (cl.job_id == null && severalJobsOn() && (cl.kind ?? 'window') !== 'window') return only(`it is a one-off${stays}`)
  if (!usual) return only('their usual price could not be read')
  const stopped = usual.owner === 'job' ? job?.stopped_on : c?.job_stopped_on
  if (stopped) return only(`this job has been stopped${stays}`)
  const cycle = usual.owner === 'job' ? job?.cycle_days : c?.cycle_days
  if (!((cycle ?? 0) > 0)) return only(`they have no repeat${stays}`)
  return { usual, only: null }
}

function cachedCustomer(id: number | undefined): Customer | null {
  if (typeof id !== 'number') return null
  try {
    return readCache<{ customers?: Customer[] }>('customers')?.data?.customers?.find((c) => c.id === id) ?? null
  } catch {
    return null
  }
}

// The customer list and the round's jobs read again once a usual price changed (Sonnet F14), so their page, Due and
// the next booking show it.
function readAgain(): void {
  void api('/api/customers', 'customers').catch(() => null)
  void loadRoundJobs().catch(() => null)
}

// A price carried on, or its Undo, that saved from the queue once signal returned (api.ts roundly:queued-landed).
if (typeof window !== 'undefined') window.addEventListener('roundly:queued-landed', (e) => {
  const d = (e as CustomEvent<{ onward?: unknown; undo?: { words?: string | null } | null }>).detail
  if (!d || (!d.onward && !d.undo)) return
  readAgain()
  if (d.undo?.words) toast(d.undo.words)
})

const radio = 'price-when'
let sheets = 0

function openPriceSheet(cl: PricedVisit, after: () => void, waitingShown: (whole: number) => void): void {
  // A visit with extras (0087): its price and extras change together from Edit visit, never here, from any menu that
  // opens this sheet (Today's Change price included: Sonnet's extras review 1).
  if (extrasOf(cl.extras_json).length) { toast(`${cl.customer_name ?? 'This visit'} has extras on this visit. Change its price from the visit: Diary, ${cl.customer_name ?? 'the visit'}, Edit visit.`); return }
  const who = cl.customer_name ?? 'This visit'
  const path = `/api/cleans/${cl.id}`
  // The inside and outside split, and the price just before this save: as opened, then as the server answered a refusal
  // with, so Save again keeps the typed outside price over the fresh inside (A3) and Undo goes back to the price just
  // before (A4; Astra's final review, 21 Oct 2026).
  let insides = hasInsides(cl) ? (cl.inside_pence as number) : 0
  let shown = insides ? outsidePence(cl) : (cl.amount_pence ?? null)
  let beforeWhole: number | null = cl.amount_pence ?? null
  const jobLabel = visitJobLabel({ customer_id: cl.customer_id ?? 0, kind: (cl.kind ?? 'window') as Clean['kind'], job_id: cl.job_id ?? null, job_name: cl.job_name ?? null })
  const s = sheet(`${who}’s price`)
  s.dialog.classList.add('price-sheet')
  const name = `${radio}-${++sheets}`

  const line = el('p', { class: 'price-sheet-line' })
  const input = el('input', { type: 'text', inputmode: 'decimal', class: 'price-sheet-input', autocomplete: 'off', 'aria-label': `${insides ? 'Outside price' : 'Price'} for ${who}, in ${currencyWord()}` }) as HTMLInputElement
  input.value = shown === null || shown === undefined ? '' : (shown / 100).toFixed(2)
  const cur = el('span', { class: 'price-sheet-cur' }, insides ? `Outside ${currencySymbol()}` : currencySymbol())
  const box = el('label', { class: 'price-sheet-box' }, cur, input)
  const inside = el('p', { class: 'price-sheet-inside' }, insides ? `Inside ${money(insides)} stays as it is.` : '')
  inside.hidden = !insides
  const choices = el('div', { class: 'price-sheet-choices' })
  const save = el('button', { type: 'button', class: 'btn-primary price-sheet-save' }, 'Save') as HTMLButtonElement
  s.body.append(line, box, inside, choices, s.error, pinFoot(save))

  // What the screen shows, and what the save carries (Astra F02, Sol R01): the usual price and its version as drawn.
  let offer = offerFor(cl, cachedCustomer(cl.customer_id), roundJobs())
  let chosen: 'just' | 'after' = 'just'
  // The visit's version this save is made on; after a refusal the person was shown, the one the server answered.
  let version = cl.version
  // Refused attempts still in the queue, dismissed before the next intentional Save (Astra F09, Sol R08), or when the
  // sheet closes: the refusal was said here, and left in the queue it would hold back the next price saved for this visit.
  const refused = new Set<string>()
  s.dialog.addEventListener('close', () => { for (const k of refused) dismiss(k) })

  const typed = (): number | null => { try { return parseMoney(input.value) } catch { return null } }
  // The two choices are made once for what the screen shows (again only if a fresh read changes it), then their words
  // follow the box and the pick, so a choice picked with the keyboard keeps its focus.
  let words: (() => void) | null = null
  const drawChoices = (): void => {
    choices.replaceChildren()
    if (offer.only !== null) {
      chosen = 'just'
      choices.append(el('p', { class: 'price-sheet-only' }, offer.only))
      words = null
      return
    }
    const usual = offer.usual
    const job = jobLabel.toLowerCase()
    const choice = (value: 'just' | 'after', title: string): { label: HTMLElement; radio: HTMLInputElement; text: HTMLElement } => {
      const radio = el('input', { type: 'radio', name, value }) as HTMLInputElement
      const pickIt = (): void => { chosen = value; paint() }
      radio.addEventListener('click', pickIt)
      radio.addEventListener('change', pickIt)
      const text = el('span', { class: 'price-choice-words' })
      return { label: el('label', { class: 'price-choice' }, radio, el('span', { class: 'price-choice-text' }, el('strong', {}, title), text)), radio, text }
    }
    const just = choice('just', 'Just this visit'), onward = choice('after', 'This visit and every one after')
    just.text.textContent = `${dayLabel(cl.date)} only. ${usual.pence != null ? `Their usual price stays ${money(usual.pence)}.` : 'They have no usual price yet.'}`
    const first = usual.owner === 'customer' && usual.firstClean != null ? el('span', { class: 'price-choice-words' }, `Their first-clean price (${money(usual.firstClean)}) stays as it is.`) : null
    if (first) onward.text.after(first)
    choices.append(just.label, onward.label)
    words = () => {
      const p = typed()
      onward.text.textContent = `Their usual price becomes ${p === null ? 'the new price' : money(p)} for ${job}. ${usual.pence != null
        ? `Later visits at ${money(usual.pence)} change too; any at another price stay as they are.`
        : 'Later visits with no price get it too; any with a price stay as they are.'}`
      for (const [c, v] of [[just, 'just'], [onward, 'after']] as const) {
        c.radio.checked = chosen === v
        c.label.classList.toggle('is-chosen', chosen === v)
      }
    }
  }
  const paint = (): void => {
    const u = offer.usual
    line.textContent = `${jobLabel} · ${u?.pence != null ? `usually ${money(u.pence)}` : 'no usual price yet'}`
    words?.()
    const p = typed()
    save.textContent = p === null ? 'Save' : `Save ${money(p)}`
  }
  const paintChoices = (): void => { drawChoices(); paint() }
  paintChoices()
  input.addEventListener('input', () => { s.error.textContent = ''; paint() })
  input.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); void doSave() } })
  save.addEventListener('click', () => void doSave())
  // Ready to type over on a computer; tapped on a phone once the sheet is up (sheet-keyboard.ts cursorOnOpen: the
  // iPhone keyboard's white screen, 20 Oct 2026).
  cursorOnOpen(input, { select: true })

  // Read fresh: the usual price and the job as they are now, shown before anyone saves (never adopted unseen).
  const readFresh = async (): Promise<void> => {
    if (typeof cl.customer_id !== 'number') return
    try {
      const fresh = await api<{ customer?: Customer; jobs?: CustomerJob[] }>(`/api/customers/${cl.customer_id}`)
      if (!s.dialog.open || !fresh.customer) return
      offer = offerFor(cl, fresh.customer, fresh.jobs ?? roundJobs())
      paintChoices()
    } catch { /* no signal: the saved copy stays on screen */ }
  }
  void readFresh()

  async function doSave(): Promise<void> {
    s.error.textContent = ''
    const pence = typedPrice(input.value, (m) => { s.error.textContent = m; input.focus() })
    if (pence === undefined) return
    const usual = offer.only === null ? offer.usual : null
    const onward = chosen === 'after' && usual !== null && pence !== usual.pence
    // Nothing changes: this visit already has it, and their usual price stays (Sonnet F12).
    if (pence === (shown ?? null) && !onward) { s.error.textContent = 'Nothing to change'; return }
    const whole = pence + insides
    for (const k of refused) dismiss(k)
    refused.clear()
    const had = new Set(getQueue().map((q) => q.key))
    let queuedKey: string | null = null
    save.disabled = true
    const ack = await mutate({
      label: `Price: ${cl.customer_name ?? ''} ${cl.date}`,
      method: 'PATCH',
      path,
      body: { version, amount_pence: whole, ...(onward && usual ? { onward: { usual_pence: usual.pence, usual_version: usual.version } } : {}) },
      onQueued: (m) => { queuedKey = m.key },
    })
    save.disabled = false
    if (ack.ok || ack.status === 0) {
      s.done()
      if (!ack.ok) waitingShown(whole)
      after()
      if (onward) { if (ack.ok) readAgain() }
      offerUndoFor(ack, pence, onward, queuedKey)
      return
    }
    if (ack.status === 409) {
      for (const q of getQueue()) if (!had.has(q.key) && q.path === path && q.status === 'conflict') refused.add(q.key)
      const now = ack.current
      // Extras put on it meanwhile (0087): this sheet can't change a visit with extras, so no Save again (GPT-6.1's review 2).
      if (now && extrasOf(now.extras_json as string | null).length) {
        s.error.textContent = `Extras were added to this visit somewhere else. Change its price from the visit: Diary, ${cl.customer_name ?? 'the visit'}, Edit visit.`
        save.disabled = true
        return
      }
      if (now && typeof now.version === 'number' && !ack.code) {
        // Someone else changed this visit first: said, the typed price kept; Save again saves it on what is there now:
        // the typed outside price over the inside as it is now (A3), and Undo then goes back to their price (A4).
        version = now.version
        const theirs = (now.amount_pence as number | null | undefined) ?? null
        const theirInside = typeof now.inside_pence === 'number' && now.inside_pence > 0 && theirs !== null ? now.inside_pence : 0
        const differs = theirs !== beforeWhole || theirInside !== insides
        insides = theirInside
        shown = theirs === null ? null : theirs - theirInside
        beforeWhole = theirs
        cur.textContent = insides ? `Outside ${currencySymbol()}` : currencySymbol()
        inside.textContent = insides ? `Inside ${money(insides)} stays as it is.` : ''
        inside.hidden = !insides
        s.error.textContent = differs
          ? `This visit changed somewhere else: its price is now ${priceWords({ amount_pence: theirs, inside_pence: theirInside || null }) ?? 'not set'}. Save again to make it ${insides ? 'outside ' : ''}${money(pence)}.`
          : 'This visit changed somewhere else. Save again to keep your price.'
        return
      }
      s.error.textContent = ack.error ?? 'This visit changed somewhere else. Close this and look again.'
      // Their usual price changed on another phone: the sheet shows it now, for the person to look again.
      if (ack.code === 'usual_changed' || ack.code === 'job_stopped' || ack.code === 'customer_gone') void readFresh()
      return
    }
    s.error.textContent = ack.error && ack.status !== 403 ? ack.error : "Couldn't save the price. Try again."
  }

  function offerUndoFor(ack: Ack, pence: number, onward: boolean, queuedKey: string | null): void {
    const waitWords = ack.ok ? '' : '. No signal: saved on this phone, it sends as soon as it can'
    if (!onward) {
      undoPatch({ text: `Changed ${who}'s ${insides ? 'outside ' : ''}price to ${money(pence)}${waitWords}`, label: `Undo price: ${cl.customer_name ?? ''} ${cl.date}`, path, ack, version,
        body: { amount_pence: beforeWhole }, after })
      return
    }
    // Undo puts back only this save's own changes, each one still as it left it, and says what it left (Astra F04,
    // Sol R02, Sonnet F3, F11).
    const key = ack.onward?.key ?? queuedKey
    if (!key) return
    const n = ack.onward?.visits.length ?? 0
    const what = ack.ok ? `: this visit, their usual price${n ? ` and ${n} later visit${n === 1 ? '' : 's'}` : ''}` : ''
    const row = ack.row as { version?: number } | undefined
    offerUndo(`${who}'s price is ${money(pence)} from now on${what}${waitWords}`, () => {
      void mutate({ label: `Undo price from now on: ${cl.customer_name ?? ''}`, method: 'PATCH', path,
        body: { version: typeof row?.version === 'number' ? row.version : version + 1, undo_onward: key } }).then((back) => {
        // No signal: it waits behind the save it undoes, and says what it left when it lands (the listener above).
        if (back.status === 0) return
        if (back.ok) readAgain()
        // What it put back is on screen; what it left, or why it did nothing ("Nothing to undo"), is said.
        const said = back.ok ? back.undo?.words : (back.error ?? 'Undo did not save. Try again.')
        if (said) toast(said)
        after()
      })
    })
  }
}
